import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useCreateHybridMeetingActions from './CreateHybridMeeting.actions.hook';
import { hybridMeetingTransformer } from './CreateHybridMeeting.transformers';
import {
  CreateHybridMeetingStepsIndex,
  CreateHybridMeetingWithEmailConfigurationStepsIndex,
} from './CreateHybridMeeting.types';
import useCreateHybridMeetingButtons from './CreateHybridMeetingButtons.hook';
import useCreateHybridMeetingStepper from './CreateHybridMeetingStepper.hook';
import { eventsRoute } from 'basics/constants/routes.constants';
import { warningToast } from 'basics/utils/toast';
import { EventStatus, Presence } from 'generated/event.types';
import { isDraft } from 'modules/events/basics/utils/events.utils';
import { EditEventButtonActionStates } from 'modules/events/components/EditEventButtonsBar/EditEventButtonsBar.types';
import useLazyGetFullPrivateEventById from 'modules/events/graphql/queries/getLazyFullPrivateEventById';
import eventState from 'modules/events/states/events.states';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCreateHybridMeeting = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const flags = useFlags();
  const yodaFormMethods = useYodaCreateForm({ setIsValidOptional: true });
  const hybridMeetingActions = useCreateHybridMeetingActions(yodaFormMethods);
  const { queryGetFullPrivateEventById, data: event, error, loading } = useLazyGetFullPrivateEventById();

  if (event && event.presence !== Presence.hybrid) {
    warningToast(t('errors_standard'));
    navigate(eventsRoute);
  }

  const buttonsConfig = useCreateHybridMeetingButtons(
    hybridMeetingActions,
    !isDraft(event?.status),
  );
  const stepsList = useCreateHybridMeetingStepper(yodaFormMethods.useWatchStep);

  const isLastStep = flags.eventsBiogenlinc4151SectionEmail
    ? hybridMeetingActions.stepIndexer.stepIndex === CreateHybridMeetingWithEmailConfigurationStepsIndex.publicationStep
    : hybridMeetingActions.stepIndexer.stepIndex === CreateHybridMeetingStepsIndex.publicationStep;

  const actionStates: EditEventButtonActionStates = {
    updateLoading: hybridMeetingActions.updateLoading,
    publishLoading: hybridMeetingActions.publishLoading,
    readyToPublish: yodaFormMethods.formState.isValid && event?.status === EventStatus.ready,
    isLastStep,
  };

  useEffect(() => {
    if (id) {
      queryGetFullPrivateEventById({ variables: { eventId: id } });
    }
  }, [id, queryGetFullPrivateEventById]);

  useEffect(() => {
    if (event) {
      eventState.createEvent = hybridMeetingTransformer(event);
    }
    if (error) {
      warningToast(t('errors_standard'));
      navigate(eventsRoute);
    }
  }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickHome = () => {
    navigate(eventsRoute);
  };

  const showLoader = loading || !event;

  return {
    t,
    handleClickHome,
    handleChangeStep: hybridMeetingActions.handleChangeStep,
    showLoader,
    yodaFormMethods,
    stepIndexer: hybridMeetingActions.stepIndexer,
    id,
    buttonsConfig,
    actionStates,
    stepsList,
    event,
  };
};

export default useCreateHybridMeeting;
